import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Container,
  Grid,
  IconButton,
  Link,
  Modal,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import { CircuitTabs } from "../CircuitOrganize";

import ListResponsive from "../../../components/other/ListResponsive";
import { getTokenStatusColor } from "../../../components/utilities/CircuitTokenUtils";

import * as Yup from "yup";
import { Formik } from "formik";

import moment from "moment";
import "moment-timezone";
import {
  BCPSquarePlus,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";
import {
  getData,
  postData,
} from "../../../components/utilities/UtilityFunctions";
import GenericPicker from "../../../components/utilities/GenericPicker";
import OrgDashWhitelist from "../../organizations/components/OrganizationDashboard/OrgDashWhitelist";
import { useDebounce } from "../../../hooks/useDebounce";

export default function CircuitTokens(props) {
  const { t } = useTranslation();
  let { leagueId } = useParams();
  const { activeTab, handleTabChange, circuit, authContext } = props;

  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState("tokens"); 

  const [tokens, setTokens] = useState(null);
  const [token, setToken] = useState(null);

  // Server-side paging (v2): one page per request, cursors from the API
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 400);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [keys, setKeys] = useState({ nextKey: null, prevKey: null });
  const requestIdRef = useRef(0);

  const fetchPage = async ({ nextKey, prevKey, newPage = 0 } = {}) => {
    // Only the latest request is allowed to update state
    const requestId = ++requestIdRef.current;
    setLoading(true);

    // Omit empty values, URLSearchParams would send them as "undefined"
    var params = {
      leagueId: leagueId,
      limit: rowsPerPage,
    };
    const search = debouncedSearch?.trim();
    if (search) params.search = search;
    if (nextKey) params.nextKey = nextKey;
    else if (prevKey) params.prevKey = prevKey;

    try {
      var response = await getData("/leaguetokens", params, "v2");
      if (requestId !== requestIdRef.current) return;
      setTokens(response?.data || []);
      setKeys({
        nextKey: response?.nextKey || null,
        prevKey: response?.prevKey || null,
      });
      setTotal(response?.total || 0);
      setPage(newPage);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      postNotification("error", err);
      setTokens((currentTokens) => currentTokens || []);
    }
    setLoading(false);
  };

  // First page, no cursor
  const refresh = () => fetchPage();

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [debouncedSearch, rowsPerPage, leagueId]);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
          }}
        >
          <Typography variant="h3">{circuit.name}</Typography>

          <CircuitTabs
            circuit={circuit}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(event, newViewMode) => {
                if (newViewMode !== null) {
                  setViewMode(newViewMode);
                  // ListResponsive remounts with an empty search box
                  setSearchText("");
                }
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "none",
                },
              }}
            >
              <ToggleButton value="tokens">
                Circuit Tokens
              </ToggleButton>
              <ToggleButton value="whitelist">
                Token Whitelists
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            p: 2,
          }}
        >
          {viewMode === "tokens" ? (
            <ListResponsive
            originalList={tokens}
            doSearch
            doPages
            initialRowsPerPage={10}
            actionButton={{
              props: {
                variant: "contained",
                color: "primary",
                fullWidth: true,
                onClick: (e) => {
                  setOpen(true);
                },
                startIcon: <BCPSquarePlus />,
              },
              label: "New Token",
            }}
            searchProps={{
              label: "Search Tokens",
            }}
            rowsPerPageArray={[10, 25, 50, 100]}
            serverPaging={{
              total,
              page,
              rowsPerPage,
              hasNext: Boolean(keys.nextKey),
              // A full page reached via prevKey can't tell it's the first page
              hasPrev: page > 0 && Boolean(keys.prevKey),
              loading,
              onNext: () =>
                fetchPage({ nextKey: keys.nextKey, newPage: page + 1 }),
              onPrev: () =>
                fetchPage({
                  prevKey: keys.prevKey,
                  newPage: Math.max(0, page - 1),
                }),
              onRowsPerPageChange: (value) => setRowsPerPage(value),
              onSearchChange: (text) => setSearchText(text),
            }}
            rowProps={{
              mapFunction: (row, index) => (
                <Grid item xs={12}>
                  <Card key={index} sx={{ mt: 2 }}>
                    <CardActionArea onClick={(e) => setToken(row)}>
                      <Box sx={{ width: "100%" }}>
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={8} lg={9}>
                            <Typography variant="body1">
                              Token
                              {`${
                                row.tokenType
                                  ? ` (${row.tokenType}): ${row.code}`
                                  : `: ${row.code}`
                              }`}
                            </Typography>
                            <Typography variant="caption">{`${
                              row.user?.id
                                ? `${row.user?.firstName} ${row.user?.lastName}`.trim() +
                                  ": "
                                : ""
                            }${row.email}`}</Typography>
                            {row.event?.id && (
                              <Typography variant="body1">
                                Event: {row.event?.name || ""}
                              </Typography>
                            )}
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={4}
                            lg={3}
                            sx={{ textAlign: "right" }}
                          >
                            <Chip
                              label={row.status.toUpperCase()}
                              size="small"
                              sx={{ width: "100%" }}
                              color={getTokenStatusColor(row.status)}
                            />
                            <Typography variant="caption">
                              Requested:{" "}
                              {moment(row.created_at).format(
                                "MMM Do, YYYY @ LT"
                              )}
                            </Typography>
                            {row.event?.eventDate && (
                              <Typography variant="body1">
                                Event Date:{" "}
                                {moment(row.event.eventDate).format(
                                  "MMM Do, YYYY"
                                )}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              ),
            }}
          />
          ) : (
            <OrgDashWhitelist
              authContext={authContext}
              organization={circuit?.gameStore}
              organize={false}
            />
          )}
        </Grid>
      </Grid>

      <TokenModal
        token={token}
        setToken={setToken}
        authContext={authContext}
        circuit={circuit}
        setTokens={setTokens}
        tokens={tokens}
      />

      <NewTokenModal
        open={open}
        setOpen={setOpen}
        authContext={authContext}
        circuit={circuit}
        refresh={refresh}
        t={t}
      />
    </Container>
  );
}

function TokenModal(props) {
  const { circuit, token, setToken, tokens, setTokens, authContext } = props;

  const [loading, setLoading] = useState(false);
  const [tokenType, setTokenType] = useState(token?.tokenType || "");

  const updateToken = async (newStatus) => {
    setLoading(true);
    try {
      var body = {
        status: newStatus,
      };
      if (tokenType) {
        body.tokenType = tokenType;
      }
      var updatedToken = await postData(
        `/leaguetokens/${token?.id}/updateStatus`,
        {
          body,
        }
      );
      var newTokens = [];
      tokens.forEach((exToken) => {
        newTokens.push(
          exToken.id === token.id
            ? {
                ...exToken,
                ...updatedToken,
                approver: {
                  lastName: authContext?.authState?.identity?.lastName,
                  firstName: authContext?.authState?.identity?.firstName,
                  email: authContext?.authState?.identity?.email,
                  id: authContext?.authState?.identity?.userId,
                },
              }
            : exToken
        );
      });
      setTokens(newTokens);
      setLoading(false);
      setToken(null);
      return;
    } catch (err) {
      postNotification("error", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    setTokenType(token?.tokenType || "");
    // eslint-disable-next-line
  }, [token]);

  return (
    <>
      <Modal
        disableAutoFocus
        open={Boolean(token)}
        onClose={() => {
          setToken(null);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{ zIndex: 990 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setToken(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={1}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ textTransform: "capitalize" }}
              >
                {token?.status} Token
              </Typography>
              {token?.event?.id && (
                <Typography variant="body1">
                  Event:{" "}
                  <Link
                    href={`/event/${token.event.id}`}
                    target="_blank"
                    underline="none"
                  >
                    {token.event?.name}
                  </Link>
                </Typography>
              )}
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 3,
            }}
          >
            {circuit?.tokenTypes?.length > 0 && (
              <Grid item xs={12}>
                <GenericPicker
                  label="Token Type"
                  required
                  inclAny={false}
                  name="tokenType"
                  list={circuit.tokenTypes}
                  value={tokenType}
                  handleChange={(e) => {
                    setTokenType(e.target?.value);
                  }}
                />
              </Grid>
            )}
            {token?.status?.toLowerCase() === "pending" && (
              <Grid item xs={12}>
                <Button
                  color="primary"
                  fullWidth
                  variant="outlined"
                  onClick={async (e) => {
                    if (circuit?.tokenTypes?.length > 0 && !tokenType?.length) {
                      postNotification(
                        "error",
                        "Please select a token type before approval"
                      );
                      return;
                    }
                    await updateToken("active");
                  }}
                >
                  Approve Token
                </Button>
              </Grid>
            )}
            {token?.status?.toLowerCase() !== "pending" &&
              token?.status?.toLowerCase() !== "active" && (
                <Grid item xs={12}>
                  <Button
                    color="primary"
                    fullWidth
                    variant="outlined"
                    onClick={async (e) => {
                      if (
                        circuit?.tokenTypes?.length > 0 &&
                        !tokenType?.length
                      ) {
                        postNotification(
                          "error",
                          "Please select a token type before approval"
                        );
                        return;
                      }
                      await updateToken("active");
                    }}
                  >
                    Reactivate Token
                  </Button>
                </Grid>
              )}
            {token?.status?.toLowerCase() !== "rejected" && (
              <Grid item xs={12}>
                <Button
                  color="primary"
                  fullWidth
                  variant="outlined"
                  onClick={async (e) => {
                    await updateToken("rejected");
                  }}
                >
                  Reject Token
                </Button>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                color="error"
                fullWidth
                variant="outlined"
                onClick={(e) => {
                  setToken(null);
                }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      {loading && <ModalLoadingFull />}
    </>
  );
}

function NewTokenModal(props) {
  const { open, setOpen, circuit, refresh, authContext, t } = props;

  const [loading, setLoading] = useState(false);

  return (
    <>
      <Modal
        disableAutoFocus
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{ zIndex: 990 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={1}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                New Token
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              email: "",
              firstName: "",
              lastName: "",
              userId: null,
              tokenType: "",
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .max(128)
                .email("Must be a valid email")
                .required("Required"),
              tokenType:
                circuit.tokenTypes?.length > 0
                  ? Yup.string().max(128).required("Required")
                  : Yup.string().nullable(),
            })}
            enableReinitialize
            onSubmit={async (values) => {
              setLoading(true);

              try {
                var newToken = {
                  leagueId: circuit.id,
                  approverId: authContext?.authState?.identity?.userId,
                  userId: values.userId,
                };

                if (values.tokenType?.length > 0) {
                  newToken.tokenType = values.tokenType;
                }

                await postData(`/leaguetokens`, {
                  body: newToken,
                });

                // Newest first on the server, so reload the first page
                refresh();
              } catch (err) {
                postNotification("error", err);
              }
              setLoading(false);
              setOpen(false);
              return;
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              isSubmitting,
              touched,
              values,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <TextField
                      name="email"
                      label="Email"
                      value={values.email}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      required
                      onChange={(e) => {
                        const trimmedString = e.target.value
                          .trim()
                          .toLowerCase();
                        setFieldValue("email", trimmedString);
                      }}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                      onBlur={async (e) => {
                        handleBlur(e);

                        var apiUser;
                        if (e.target.value !== "") {
                          setLoading(true);
                          try {
                            apiUser = await getData(
                              `/users/${e.target.value}`,
                              {
                                queryStringParameters: {},
                              }
                            );
                          } catch (err) {}
                          setLoading(false);
                        }

                        setFieldValue("userId", apiUser?.id || null);
                        setFieldValue("firstName", apiUser?.firstName || "");
                        setFieldValue("lastName", apiUser?.lastName || "");

                        if (!apiUser?.id) {
                          postNotification(
                            "error",
                            t("notifications.errors.userNotFound")
                          );
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="First Name"
                      value={values.firstName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Last Name"
                      value={values.lastName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  {circuit.tokenTypes?.length > 0 && (
                    <Grid item xs={12}>
                      <GenericPicker
                        label="Token Type"
                        required
                        inclAny={false}
                        name="tokenType"
                        list={circuit.tokenTypes}
                        value={values.tokenType}
                        handleChange={handleChange}
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      color="error"
                      fullWidth
                      variant="outlined"
                      onClick={(e) => {
                        setOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      color="success"
                      fullWidth
                      variant="contained"
                      type="submit"
                      disabled={!values.userId}
                    >
                      Send Token
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
      {loading && <ModalLoadingFull />}
    </>
  );
}
